import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import { projectFns, runProjectsConvexMutation } from "@/v2Projects/convexClient";
import { addConvexBreadcrumb, captureConvexError } from "@/v2Projects/telemetry";
import type { ChatMessageAttachment, NoteKind, ProjectSide } from "@/v2Projects/types";
import type { Ref } from "vue";

interface SendMessageInput {
  threadId: string;
  content: string;
  attachments: ChatMessageAttachment[];
  replyTo?: string;
}

interface SendNoteInput {
  threadId: string;
  noteKind: NoteKind;
  content: string;
  attachments: ChatMessageAttachment[];
}

interface UploadInput {
  files: File[];
  actingOrgId: number;
}

export function useChatActions(
  projectId: Ref<number | undefined>,
  side: Ref<ProjectSide>,
  directThreadId?: Ref<string | null>,
) {
  const api = useApi();
  const queryClient = useQueryClient();

  const sendMessage = useMutation({
    mutationFn: async (input: SendMessageInput) => {
        addConvexBreadcrumb({
          operation: "chat.send_message_started",
          projectId: projectId.value,
          threadId: input.threadId,
          side: side.value,
          functionName: "chat.mutations.sendMessage",
          counts: { attachments: input.attachments.length },
          extra: { hasReplyTo: Boolean(input.replyTo) },
        });
        return await runProjectsConvexMutation(
          projectFns.sendProjectMessage,
          {
            thread_id: input.threadId as never,
            content: input.content,
            attachments: input.attachments,
            reply_to: input.replyTo ? (input.replyTo as never) : undefined,
          },
          {
            operation: "chat.send_message",
            functionName: "chat.mutations.sendMessage",
            onError: (error, context) => {
              captureConvexError(error, {
                operation: "chat.send_message_failed",
                projectId: projectId.value,
                threadId: input.threadId,
                side: side.value,
                level: context.isRecoverable ? "warning" : "error",
                functionName: "chat.mutations.sendMessage",
                auth: {
                  isRecoverable: context.isRecoverable,
                  recoveryAttempt: context.recoveryAttempt,
                },
                counts: { attachments: input.attachments.length },
                extra: { hasReplyTo: Boolean(input.replyTo) },
              });
            },
          },
        );
    },
    onSuccess: () => {
      if (!projectId.value) return;
      queryClient.invalidateQueries({
        queryKey: ["v2projects", "detail", projectId.value],
      });
      queryClient.invalidateQueries({
        queryKey: ["v2projects", "attachments", projectId.value],
      });
    },
  });

  const sendNote = useMutation({
    mutationFn: async (input: SendNoteInput) => {
        addConvexBreadcrumb({
          operation: "chat.send_note_started",
          projectId: projectId.value,
          threadId: input.threadId,
          side: side.value,
          functionName: "chat.mutations.sendNote",
          counts: { attachments: input.attachments.length },
          extra: { noteKind: input.noteKind },
        });
        return await runProjectsConvexMutation(
          projectFns.sendProjectNote,
          {
            thread_id: input.threadId as never,
            note_kind: input.noteKind,
            content: input.content,
            attachments: input.attachments,
          },
          {
            operation: "chat.send_note",
            functionName: "chat.mutations.sendNote",
            onError: (error, context) => {
              captureConvexError(error, {
                operation: "chat.send_note_failed",
                projectId: projectId.value,
                threadId: input.threadId,
                side: side.value,
                level: context.isRecoverable ? "warning" : "error",
                functionName: "chat.mutations.sendNote",
                auth: {
                  isRecoverable: context.isRecoverable,
                  recoveryAttempt: context.recoveryAttempt,
                },
                counts: { attachments: input.attachments.length },
                extra: { noteKind: input.noteKind },
              });
            },
          },
        );
    },
    onSuccess: () => {
      if (!projectId.value) return;
      queryClient.invalidateQueries({
        queryKey: ["v2projects", "detail", projectId.value],
      });
      queryClient.invalidateQueries({
        queryKey: ["v2projects", "attachments", projectId.value],
      });
    },
  });

  const uploadAttachments = useMutation({
    mutationFn: async (input: UploadInput) => {
      try {
        if (directThreadId?.value) {
          return await api.directChatrooms.uploadAttachments({ threadId: directThreadId.value, files: input.files });
        }
        if (!projectId.value) throw new Error("No conversation selected.");
        const projectsApi =
          side.value === "expert" ? api.expert.projectsV2 : api.client.projectsV2;
        addConvexBreadcrumb({
          operation: "chat.attachments_upload_started",
          projectId: projectId.value,
          side: side.value,
          counts: { files: input.files.length },
          extra: { actingOrgId: input.actingOrgId },
        });
        return await projectsApi.uploadAttachments({
          params: { projectId: projectId.value },
          body: {
            acting_org_id: input.actingOrgId,
            files: input.files,
          },
        });
      } catch (error) {
        captureConvexError(error, {
          operation: "chat.attachments_upload_failed",
          projectId: projectId.value,
          side: side.value,
          counts: { files: input.files.length },
          extra: { actingOrgId: input.actingOrgId },
        });
        throw error;
      }
    },
    onSuccess: () => {
      if (!projectId.value) return;
      queryClient.invalidateQueries({
        queryKey: ["v2projects", "attachments", projectId.value],
      });
    },
  });

  return { sendMessage, sendNote, uploadAttachments };
}
